import moment from "moment";
import { Permissions } from "#/Utilities/Domain/Users/Permissions.tsx";
import { assertNever } from "../../../../types/internal/utility";
import Request from "../../../Utilities/Platform/Request";
import { type FileOrderType } from "./FileViewer";
import { type FolderListType } from "./MediaSelector";

export interface MediaDirectoryType {
    name: string;
    folders: Array<MediaFolderType>;
    fileCount: number;
}

export interface MediaSelectedType {
    type: MediaItemType;
    id: string;
    name: string;
    index: number;
}

export interface MediaFolderType {
    uuid: string;
    folderId: null | string;
    name: string;
    hasChildren: boolean;
    createdAt: string;
    updatedAt: string | null;
    count: number;
}

export interface MediaFileType {
    uuid: string;
    folderId: string;
    name: string;
    size: number;
    mimeType: null | string;
    url: string;
    thumbnail: string;
    extension: string | undefined;
    type: MediaType | null;
    connections: Array<FileConnectionType>;
    createdAt: string;
    updatedAt: string | undefined;
}

export interface MediaPathType {
    id: string;
    name: string;
}

export enum MediaType {
    Image = "IMAGE",
    Video = "VIDEO",
}

export enum MediaItemType {
    File = "file",
    Folder = "folder",
}

export type MediaFilePages = Array<Array<MediaFileType> | null>;

/**
 * Marks a drag as originating from the media grid so the upload overlay knows to ignore it.
 *
 * The payload itself is held in React state rather than on the DataTransfer, as the contents of a
 * DataTransfer can only be read during the drop event, not while deciding whether a tile can accept it.
 */
export const MEDIA_DRAG_MIME = "application/x-shopfront-media";

export interface MediaDragType {
    type: MediaItemType;
    items: Array<MediaSelectedType>;
}

interface MediaMoveResultType {
    item: MediaSelectedType;
    name: string;
}

export interface MediaMoveSummaryType {
    moved: Array<MediaMoveResultType>;
    failed: Array<MediaSelectedType>;
}

/**
 * Moves the given items into a folder, `null` being the home root.
 *
 * The server renames anything that would clash with a name already in the destination, so the resulting
 * name is returned alongside each item that moved.
 */
export async function moveItems(
    items: Array<MediaSelectedType>,
    directory: string | null
): Promise<MediaMoveSummaryType> {
    const moved: Array<MediaMoveResultType> = [];
    const failed: Array<MediaSelectedType> = [];

    for(const item of items) {
        try {
            const response = await Request.put<{
                success: boolean;
                name: string;
            }>(`/media/${item.id}/${item.type}/move`, {
                directory,
            });

            moved.push({
                item,
                name: response.data.name,
            });
        } catch(err) {
            console.error("There was an issue trying to move the item", err);

            failed.push(item);
        }
    }

    return {
        moved,
        failed,
    };
}

const MAX_VIDEO_FILE_SIZE = 16 * 1024 * 1024; // 16MiB
const MAX_IMAGE_FILE_SIZE = 8 * 1024 * 1024; // 8MiB

export const MEDIA_URL = "https://sasonline-cloudpos.s3";

const IMAGE_MIME = [
    "image/png",
    "image/gif",
    "image/jpeg",
    "image/webp",
];

const VIDEO_MIME = [
    "video/webm",
    "video/mp4",
];

function getFilters(filters: Array<MediaType> = []): Array<string> {
    const results: Array<string> = [];

    for(const filter of filters) {
        if(filter === MediaType.Video) {
            results.push(...VIDEO_MIME);
        } else if(filter === MediaType.Image) {
            results.push(...IMAGE_MIME);
        } else {
            assertNever(filter);
        }
    }

    return [ ...new Set(results) ];
}

export const IMAGE_EXTENSIONS = [
    "png", "jpg", "jpeg", "webp", "gif", "jfif",
];

const VIDEO_EXTENSIONS = [
    "mp4", "webm",
];

function getFileExtension(file: string): string | undefined {
    return file.split(".").pop();
}

export function getFileType(extension: string | undefined): MediaType | null {
    if(typeof extension === "undefined") {
        return null;
    }

    if(IMAGE_EXTENSIONS.includes(extension.toLowerCase())) {
        return MediaType.Image;
    } else if(VIDEO_EXTENSIONS.includes(extension.toLowerCase())) {
        return MediaType.Video;
    }

    return null;
}

export function uploadURL(path: Array<MediaPathType>, url: string): Promise<Array<MediaFileType>> {
    const data: {
        file_url: string;
        directory?: string;
    } = {
        "file_url": url,
    };

    if(path.length > 1) {
        data.directory = path[path.length - 1].id;
    }

    return Request.post<Array<ServerFileType>>("/media/upload", data)
        .then(response => {
            return response.data.map(transformServerFile);
        });
}

export async function uploadFileList(
    path: Array<MediaPathType>,
    files: FileList,
    onProgress: (id: string, name: string, event: ProgressEvent | true) => void
): Promise<Array<MediaFileType>> {
    // Validate all the files and add to the form data
    for(let i = 0, l = files.length; i < l; i++) {
        if(IMAGE_MIME.includes(files[i].type)) {
            if(files[i].size > MAX_IMAGE_FILE_SIZE) {
                return Promise.reject("File size exceeds maximum");
            }
        } else if(VIDEO_MIME.includes(files[i].type)) {
            if(files[i].size > MAX_VIDEO_FILE_SIZE) {
                return Promise.reject("File size exceeds maximum");
            }
        } else {
            return Promise.reject("Invalid file type");
        }
    }

    const results: Array<MediaFileType> = [];

    // Fire a single request per file
    let requests: Array<Promise<void>> = [];

    for(let i = 0, l = files.length; i < l; i++) {
        const formData = new FormData();

        formData.append("file0", files[i]);

        if(path.length > 1) {
            formData.append("directory", path[path.length - 1].id);
        }

        const id = Date.now().toString();
        const name = files[i].name;

        requests.push(
            Request.post<Array<ServerFileType>>("/media/upload", formData, {
                onUploadProgress: progress => onProgress(id, name, progress),
                headers         : {
                    "Content-Type": "multipart/form-data",
                },
            })
                .then(response => {
                    onProgress(id, name, true);

                    for(const file of response.data) {
                        results.push(transformServerFile(file));
                    }
                })
        );

        if(requests.length > 4) {
            await Promise.all(requests);
            requests = [];
        }
    }

    await Promise.all(requests);

    return results;
}

export function createDirectory(path: Array<MediaPathType>, name: string): Promise<{
    uuid: string;
    name: string;
}> {
    if(path?.[0]?.id !== "home") {
        return Promise.reject("Permission Denied");
    }

    return Permissions.can("create_media_folder")
        .then(permission => {
            if(!permission) {
                return Promise.reject("Permission Denied");
            }

            let folder: string | null = path[path.length - 1].id;

            if(folder === "home") {
                folder = null;
            }

            return Request.post<{
                uuid: string;
                name: string;
            }>("/media/folder", {
                directory: folder,
                name,
            })
                .then(response => {
                    return response.data;
                });
        });
}

export const MEDIA_FILE_PAGE_SIZE = 100;

export function getFile(index: number, pages: MediaFilePages): MediaFileType | null {
    const pageIndex = getPageIndex(index);

    const page = pages[pageIndex];

    if(!page) {
        return null;
    }

    return page[index % MEDIA_FILE_PAGE_SIZE] ?? null;
}

export function getPageIndex(index: number): number {
    return Math.floor(index / MEDIA_FILE_PAGE_SIZE);
}

export async function loadFolder(
    path: Array<MediaPathType>,
    search: string,
    filters: Array<MediaType> = []
): Promise<MediaDirectoryType> {
    const isMDB = path?.[0]?.id === "mdb";

    let id: string | null;

    if(path.length === 1) {
        id = null;
    } else {
        id = path[path.length - 1].id;
    }

    return Request.get<{
        name: string;
        folders: Array<{
            uuid: string;
            folder_id: null | string;
            name: string;
            has_folders: boolean;
            has_files: boolean;
            created_at: string;
            updated_at: string | null;
            count: number;
        }>;
        fileCount: number;
    }>(
        "/media/folder",
        {
            params: {
                directory: id,
                mdb      : isMDB,
                filters  : getFilters(filters),
                search,
            },
        }
    )
        .then(response => {
            return {
                name     : response.data.name,
                fileCount: response.data.fileCount,
                folders  : response.data.folders.map(folder => ({
                    uuid       : folder.uuid,
                    folderId   : folder.folder_id,
                    name       : folder.name,
                    hasChildren: folder.has_files || folder.has_folders,
                    createdAt  : folder.created_at,
                    updatedAt  : folder.updated_at,
                    count      : folder.count,
                })),
            };
        });
}

export interface FileConnectionType {
    type: string;
    id: string;
}

interface ServerFileType {
    uuid: string;
    folder_id: string;
    name: string;
    size: number;
    mime_type: null | string;
    url: string;
    thumbnail: string;
    connections: Array<FileConnectionType>;
    created_at: string;
    updated_at: string | undefined;
}

function transformServerFile(file: ServerFileType): MediaFileType {
    const extension = getFileExtension(file.url);

    return {
        uuid       : file.uuid,
        folderId   : file.folder_id,
        name       : file.name,
        size       : file.size,
        mimeType   : file.mime_type,
        url        : file.url,
        thumbnail  : file.thumbnail,
        type       : getFileType(extension),
        connections: file.connections,
        createdAt  : file.created_at,
        updatedAt  : file.updated_at,
        extension,
    };
}

export function loadItemsPage(
    path: Array<MediaPathType>,
    search: string,
    order: FileOrderType,
    filters: Array<MediaType> = [],
    after = 0
): Promise<Array<MediaFileType>> {
    const isMDB = path?.[0]?.id === "mdb";

    let id: string | null;

    if(path.length === 1) {
        id = null;
    } else {
        id = path[path.length - 1].id;
    }

    return Request.get<{
        files: Array<ServerFileType>;
    }>("/media/files", {
        params: {
            directory: id,
            mdb      : isMDB,
            by       : order.value.value,
            direction: order.dir.toLowerCase(),
            filters  : getFilters(filters),
            search,
            after,
        },
    })
        .then(response => {
            return response.data.files.map(transformServerFile);
        });
}

export async function loadDirectory(
    path: Array<MediaPathType>,
    search: string,
    order: FileOrderType,
    filters: Array<MediaType> = []
): Promise<{
    folder: MediaDirectoryType;
    files: Array<MediaFileType>;
}> {
    if(!path.length) {
        const home: MediaFolderType = {
            count      : 0,
            uuid       : "home",
            name       : "Home",
            hasChildren: true,
            folderId   : null,
            updatedAt  : null,
            createdAt  : new Date().toISOString(),
        };

        const mdb: MediaFolderType = {
            count      : 0,
            uuid       : "mdb",
            name       : "MDB",
            hasChildren: true,
            folderId   : null,
            updatedAt  : null,
            createdAt  : new Date().toISOString(),
        };

        return {
            files : [],
            folder: {
                name     : "Root",
                fileCount: 0,
                folders  : [ home, mdb ],
            },
        };
    }

    const [ folder, files ] = await Promise.all([
        loadFolder(path, search, filters),
        loadItemsPage(path, search, order, filters),
    ]);

    return {
        folder,
        files,
    };
}

export function sortItems<T extends MediaFolderType | MediaFileType>(
    items: Array<T>,
    order: FileOrderType
): Array<T> {
    const results: Array<T> = [ ...items ];

    const sortType = order.value.value;

    results.sort((a, b) => {
        if(sortType === "file_type") {
            // Check if type is directory
            if(!(
                "type" in a &&
                "type" in b
            )) {
                return a.name.localeCompare(b.name);
            }

            // Sort files by type then name
            if(a.type === b.type) {
                return a.name.localeCompare(b.name);
            } else {
                if(a.type === null) {
                    return 1;
                } else if(b.type === null) {
                    return -1;
                }

                return a.type.localeCompare(b.type);
            }
        } else if(sortType === "created_at") {
            return moment(a.createdAt).diff(moment(b.createdAt));
        } else if(sortType === "last_modified") {
            const aComp = a?.updatedAt ?? a.createdAt;
            const bComp = b?.updatedAt ?? b.createdAt;

            return moment(aComp).diff(moment(bComp));
        } else {
            // Sort by name
            return a.name.localeCompare(b.name);
        }
    });

    if(order.dir === "ASC") {
        results.reverse();
    }

    return results;
}

const MEDIA_CENTRE_OPTION_PREFIX = "_media_centre_option";

const MEDIA_CENTRE_OPTIONS = [
    "folders",
    "current",
    "search",
    "order",
] as const;

type MediaCentreOptions = typeof MEDIA_CENTRE_OPTIONS[number];

interface MediaCentreOptionType extends Record<MediaCentreOptions, unknown> {
    folders: FolderListType;
    current: Array<MediaPathType>;
    search: string;
    order: FileOrderType;
}

export function setMediaCentre<O extends MediaCentreOptions>(
    option: O,
    value: MediaCentreOptionType[O]
): void {
    sessionStorage.setItem(`${MEDIA_CENTRE_OPTION_PREFIX}_${option}`, JSON.stringify(value));
}

export function getMediaCentre<O extends MediaCentreOptions>(
    option: O,
    defaultValue: MediaCentreOptionType[O]
): MediaCentreOptionType[O] {
    const value = sessionStorage.getItem(`${MEDIA_CENTRE_OPTION_PREFIX}_${option}`);

    if(!value) {
        return defaultValue;
    }

    try {
        return JSON.parse(value);
    } catch {
        return defaultValue;
    }
}

export function clearMediaCentre(): void {
    for(const option of MEDIA_CENTRE_OPTIONS) {
        sessionStorage.removeItem(`${MEDIA_CENTRE_OPTION_PREFIX}_${option}`);
    }
}
